import { Center, Spinner, Text, VStack } from "@chakra-ui/react"
import React, { useCallback, useEffect, useState } from "react"
import { PermissionsGroupsByKeywordQueryVariables, usePermissionsGroupsByKeywordQuery } from "../../graphql"
import { PermissionsGroupsList } from "./PermissionsGroupsList"

export type PermissionsGroupsByKeywordProps = {
	keyword: string
}

export const PermissionsGroupsByKeyword: React.FC<PermissionsGroupsByKeywordProps> = ({ keyword }) => {
	const [filter, setFilter] = useState({ keyword })

	const [pagination, setPagination] = useState<PermissionsGroupsByKeywordQueryVariables["pagination"]>({ limit: 20, page: 1 })

	const [{ data, fetching, error }] = usePermissionsGroupsByKeywordQuery({
		variables: { ...filter, pagination },
	})

	useEffect(() => {
		const timeoutId = setTimeout(() => {
			setFilter({ keyword })
		}, 1000)

		return () => {
			clearTimeout(timeoutId)
		}
	}, [keyword])

	const nextPage = useCallback(() => {
		if (fetching) return

		if (data?.permissionsGroupsByKeyword.hasNextPage) {
			setPagination((prev) => ({
				...prev,
				page: (prev.page || 0) + 1,
			}))
		}
	}, [data, fetching])

	return (
		<VStack w="full" maxW="2xl" align="stretch">
			{fetching && !data?.permissionsGroupsByKeyword ? (
				<Center w="full" py="4">
					<VStack w="full" color="grayscale.label">
						<Text fontSize="sm">Loading groups</Text>
						<Spinner size="sm" />
					</VStack>
				</Center>
			) : error ? (
				<Center py="4">
					<Text fontSize="sm" fontWeight="semibold" color="error.500">
						{error.message.replace("[GraphQL] ", "")}
					</Text>
				</Center>
			) : data?.permissionsGroupsByKeyword.permissionsGroups && data.permissionsGroupsByKeyword.permissionsGroups.length ? (
				<VStack w="full" align="stretch">
					<Text>
						Showing {data.permissionsGroupsByKeyword.permissionsGroups.length || 0} group
						{(data.permissionsGroupsByKeyword.permissionsGroups.length || 0) > 1 ? "s" : ""}
					</Text>

					<PermissionsGroupsList permissionsGroups={data.permissionsGroupsByKeyword.permissionsGroups} loadMore={nextPage} />

					{fetching && (
						<Center w="full" py="4">
							<VStack w="full" color="grayscale.label">
								<Text fontSize="sm">Loading more groups</Text>
								<Spinner size="sm" />
							</VStack>
						</Center>
					)}
				</VStack>
			) : (
				<Center py="4">
					<Text fontSize="sm" fontWeight="semibold" color="error.500">
						Couldn&apos;t find any groups.
					</Text>
				</Center>
			)}
		</VStack>
	)
}
